import { Chip } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { Panel } from "@/src/pages/Dashboard/components/Panel";
import { QUEUE_PREVIEW } from "@/src/pages/Dashboard/hooks/useModerationQueue";
import type { SlaLevel } from "@/src/pages/Dashboard/sla";
import { sla, slaDuration } from "@/src/pages/Dashboard/sla";
import {
  REASON_COLOR,
  REASON_KEY,
  TARGET_KEY,
} from "@/src/pages/Reports/reportLabels";
import type { Report } from "@/src/pages/Reports/reports.type";
import { ROUTES } from "@/src/routes/paths";

/**
 * L'âge d'un dossier se teinte de son échéance.
 *
 * La couleur ne porte rien à elle seule : « il y a 31 heures » se lit déjà en
 * toutes lettres. Elle ne fait qu'attirer l'œil sur la ligne qu'il faut ouvrir
 * en premier.
 */
const AGE_CLASS = {
  fresh: "text-muted",
  soon: "text-warning",
  late: "text-danger",
} as const satisfies Record<SlaLevel, string>;

type QueuePanelProps = {
  reports: Report[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Les plus anciens signalements en attente.
 *
 * ⚠️ **L'ordre est celui du serveur — du plus ancien au plus récent — et on ne
 * le retrie pas.** C'est le sens de la file : le premier de la liste est celui
 * dont l'échéance tombe en premier.
 *
 * Les lignes ne sont pas cliquables, et c'est délibéré : un signalement n'a pas
 * d'écran à lui, seule la file en a un. Ce qui est cliquable ici mène quelque
 * part — le signaleur vers son dossier, le titre du panneau vers la file.
 */
export function QueuePanel({ reports, isLoading, isError }: QueuePanelProps) {
  const t = useTranslations("dashboard");
  const tr = useTranslations("reports");
  const { locale } = useLocale();

  const now = Date.now();

  return (
    <Panel
      emptyLabel={t("queue-empty")}
      errorLabel={t("panel-error")}
      href={ROUTES.reports}
      isEmpty={reports.length === 0}
      isError={isError}
      isLoading={isLoading}
      rows={QUEUE_PREVIEW}
      seeAll={t("see-all")}
      seeAllLabel={t("see-all-queue")}
      title={t("queue-title")}
    >
      {reports.map((report) => {
        const state = sla(report.createdAt, now);
        const level = state?.level ?? "fresh";

        return (
          <li
            className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5"
            key={report.id}
          >
            <span className="text-sm font-medium">
              {tr(TARGET_KEY[report.targetType])}{" "}
              <span className="text-muted">#{report.id}</span>
            </span>

            <Chip color={REASON_COLOR[report.reason]} size="sm">
              {tr(REASON_KEY[report.reason])}
            </Chip>

            {/* `tabular-nums` ici, et pas sur les grands nombres : c'est une
                colonne, les attentes doivent s'aligner d'une ligne à l'autre.

                ⚠️ Une **durée**, pas un « hier » : c'est le même compte que
                celui de la jauge du bandeau, et il se lit des deux côtés de
                l'échéance. */}
            <span
              className={`ms-auto shrink-0 text-xs tabular-nums ${AGE_CLASS[level]}`}
            >
              {state ? slaDuration(state.hours, locale) : null}
            </span>

            {/* ⚠️ Un `div` et non un `p` : `AccountBadge` porte un avatar, donc
                un élément de bloc, qu'un paragraphe ne peut pas contenir. */}
            <div className="flex w-full min-w-0 items-center gap-1.5 text-xs text-muted">
              {t("reported-by")}
              <AccountBadge account={report.reporter} isLinked />
            </div>
          </li>
        );
      })}
    </Panel>
  );
}
